import React, { useRef, useState } from "react";
import { X, Camera } from "lucide-react";

const MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const ACCEPTED = new Set(["image/jpeg", "image/png", "image/jpg"]);

/**
 * Premium chart-screenshot uploader for the Add/Edit Trade form.
 * - `value`: data URL string or null/empty
 * - `onChange(dataUrl | null)`: parent state updater
 */
export default function ScreenshotUploader({ value, onChange }) {
  const inputRef = useRef(null);
  const [error, setError] = useState("");
  const [hover, setHover] = useState(false);
  const has = Boolean(value);

  const handleFile = (file) => {
    setError("");
    if (!file) return;
    if (!ACCEPTED.has(file.type)) {
      setError("Only JPG/PNG files accepted");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("Image too large. Max size is 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => setError("Could not read the file. Please try again.");
    reader.onload = () => onChange(typeof reader.result === "string" ? reader.result : null);
    reader.readAsDataURL(file);
  };

  const onPick = (e) => handleFile(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setHover(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div className="space-y-2 md:col-span-2" data-testid="screenshot-uploader">
      <label className="text-xs font-semibold tracking-[0.15em] uppercase text-[var(--bt-text-muted)]">
        Chart Screenshot{" "}
        <span className="normal-case tracking-normal text-[var(--bt-text-muted)]/70 font-normal">
          (optional)
        </span>
      </label>

      {has ? (
        <div
          data-testid="screenshot-preview"
          className="relative inline-block rounded-xl overflow-hidden border"
          style={{ borderColor: "#1e1e1e", background: "#0f0f0f", padding: "8px" }}
        >
          <img
            src={value}
            alt="Trade chart screenshot"
            className="block"
            style={{
              maxHeight: "200px",
              maxWidth: "100%",
              borderRadius: "8px",
              objectFit: "contain",
            }}
          />
          <button
            type="button"
            data-testid="screenshot-remove-btn"
            onClick={() => { onChange(null); setError(""); if (inputRef.current) inputRef.current.value = ""; }}
            aria-label="Remove screenshot"
            className="absolute top-1 right-1 inline-flex items-center justify-center w-7 h-7 rounded-full transition-all duration-150 hover:scale-110"
            style={{ background: "rgba(0, 0, 0, 0.7)", color: "#ffffff", border: "1px solid rgba(255,255,255,0.2)" }}
          >
            <X className="w-3.5 h-3.5" strokeWidth={2.5} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          data-testid="screenshot-upload-zone"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setHover(true); }}
          onDragLeave={() => setHover(false)}
          onDrop={onDrop}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          className="w-full flex flex-col items-center justify-center transition-all duration-150 ease-out cursor-pointer"
          style={{
            border: `2px dashed ${hover ? "#00ff88" : "#2a2a2a"}`,
            borderRadius: "12px",
            padding: "24px",
            background: hover ? "#0f1a14" : "#0f0f0f",
          }}
        >
          <span aria-hidden className="text-3xl mb-2 leading-none">📸</span>
          <p className="text-white text-sm font-semibold" style={{ fontSize: "14px" }}>
            Add Chart Screenshot
          </p>
          <p className="text-[#6b7280] mt-1" style={{ fontSize: "12px" }}>
            PNG or JPG · Max 5MB · Optional
          </p>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png"
        onChange={onPick}
        data-testid="screenshot-file-input"
        className="hidden"
      />

      {error && (
        <p
          data-testid="screenshot-error"
          className="text-xs"
          style={{ color: "#ef4444" }}
        >
          {error}
        </p>
      )}
    </div>
  );
}

/* Tiny helper to access the icon name without leaving an unused import warning. */
export const ScreenshotCameraIcon = Camera;
